<script setup>
import i18n from '@/plugins/i18n';

import { useMediaStore } from '@/stores/media';
import { useSnackBarStore } from '@/stores/snackbar';

const props = defineProps({
  initData: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['handleDoubleClick', 'setSelectedFile']);
const { t } = i18n.global;
const emitter = inject('emitter');

const media = useMediaStore();
const notification = useSnackBarStore();

const is_init = ref(false);

const data = reactive({
  filter: {
    group_ids: [],
    search: null,
    page: 1,
    perPage: 20,
  },
  last_page: null,
  loading: false,
  selectedFile: null,
  uploadFileProgress: [],
  fileTypes: [
    {
      key: 'picture',
      value: t('images'),
    },
    {
      key: 'document',
      value: t('documents'),
    },
    {
      key: 'audio',
      value: t('audios'),
    },
    {
      key: 'video',
      value: t('videos'),
    },
  ],
});

onUnmounted(() => {
  emitter.off('media');
});

onMounted(async () => {
  emitter.on('media', files => {
    files.forEach(file => {
      saveFile(file);
      media.files.unshift(file);
    });
  });

  if (props.initData) {
    startInitData();
  }
});

function startInitData() {
  is_init.value = true;
  if (media.getAttachmentCategories.length) {
    data.attachmentTypes = media.getAttachmentCategories;
  } else {
    if (media.loadAttachmentTypes) {
      setTimeout(() => {
        data.attachmentTypes = media.getAttachmentCategories;
      }, 2000);
    } else {
      getAttachmentCategories();
    }
  }
  getData();
}

function loadMore() {
  if (data.last_page && data.last_page >= data.filter.page) {
    data.filter.page++;
    getData(true);
  }
}

const saveFile = async file => {
  let form = new FormData();
  form.set('attachment_group_id', 1);
  form.set('name', file.name);
  form.set('attachment', file);

  const config = {
    onUploadProgress: function (progressEvent) {
      let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      data.uploadFileProgress[media.files.indexOf(file)] = true;
      if (percentCompleted == 0) {
        delete data.uploadFileProgress[media.files.indexOf(file)];
      }
    },
  };

  await media
    .saveFiles(form, null, null, config)
    .then(response => {
      media.files[media.files.indexOf(file)] = response.data.data;
    })
    .catch(error => {
      notification.responseError(error, () => {
        media.files.splice(media.files.indexOf(file), 1);
      });
    });
};

const getData = async (loadMore = false) => {
  let params = {
    page: loadMore ? data.filter.page : 1,
    per_page: data.filter.perPage,
    sort: 'id',
    descending: true,
  };
  if (data.filter.search) {
    params.search = data.filter.search;
  }
  if (data.filter.group_ids.length) {
    params.group_ids = data.filter.group_ids;
  }
  data.loading = true;
  await media
    .getFilesList(params, loadMore)
    .then(response => {
      data.last_page = response.data.last_page;
    })
    .finally(() => {
      data.loading = false;
    });
};

const getAttachmentCategories = async () => {
  let params = {
    page: 1,
    per_page: 100,
    sort: 'id',
  };
  await media.getAllAttachmentCategories(params);
};

function selectImage(image) {
  data.selectedFile = image;
  emit('setSelectedFile', image);
}

function handleDoubleClick(image) {
  data.selectedFile = image;
  emit('setSelectedFile', image);
  emit('handleDoubleClick', image);
  notification.success(t('imgAdded'));
}

const onDropFiles = event => {
  event.preventDefault();

  let files = event.dataTransfer.files;
  for (let i = 0; i < files.length; i++) {
    let file = files.item(i);
    saveFile(file);
    media.files.unshift(file);
  }

  onDragLeave(event);
};

const onDragLeave = event => {
  event.preventDefault();

  let parent = event.srcElement;
  if (!parent.classList.contains('u-parent-dragdrop')) {
    parent = parent.closest('div.u-parent-dragdrop');
  }

  let mask = parent.querySelector('div.image-dragdrop-mask');
  mask.style.display = 'none';
};

const onDragOver = event => {
  event.preventDefault();

  let parent = event.srcElement;
  if (!parent.classList.contains('u-parent-dragdrop')) {
    parent = parent.closest('div.u-parent-dragdrop');
  }

  let mask = parent.querySelector('div.image-dragdrop-mask');
  mask.style.display = 'flex';
};

function getLazySrc(file, isLazy = false) {
  let type = '';
  let url = '';
  if (file.id) {
    type = file.attachment_type === 'picture' ? 'image' : file.attachment_type;
    url = file.image?.thumb_url;
  } else {
    type = file.type;
    if (!isLazy) {
      return file.progress;
    }
    url = '/assets/svg/ic_image.svg';
  }
  if (type.indexOf('document') !== -1) {
    url = '/assets/svg/ic-document.svg';
    if (file.extension == 'pdf') {
      url = '/assets/svg/ic-pdf.svg';
    }
  } else if (type.indexOf('video') !== -1) {
    url = '/assets/svg/ic-video.svg';
  } else if (type.indexOf('audio') !== -1) {
    url = '/assets/svg/ic-audio.svg';
  }
  return url;
}

const selectedFile = event => {
  let files = Object.values(event.target.files);
  files.forEach(file => {
    saveFile(file);
    media.files.unshift(file);
  });
};

const getSelectedImage = () => {
  return data.selectedFile;
};
const openFile = () => {
  document.getElementById('file-input').click();
};

watch(props, () => {
  if (props.initData && !is_init.value) {
    startInitData();
  }
});

defineExpose({ getSelectedImage });
</script>

<template>
  <div class="h-100 overflow-y-auto overflow-x-hidden px-5 py-2">
    <VRow class="pa-0 ma-0">
      <VCol cols="12" md="5">
        <VTextField
          v-model="data.filter.search"
          :label="t('searchHere')"
          bg-color="input-background"
          variant="outlined"
          density="compact"
          clearable
          @keyup.enter="getData(false)" />
      </VCol>
      <VCol cols="8" md="5">
        <VAutocomplete
          v-model="data.filter.group_ids"
          :label="t('categories')"
          outlined
          multiple
          variant="outlined"
          density="compact"
          item-title="name"
          item-value="id"
          clearable
          :items="media.getAttachmentCategories"
          @update:model-value="getData(false)" />
      </VCol>
      <VCol cols="4" md="2" class="d-flex align-center justify-end">
        <VBtn color="primary" :loading="data.loading" @click="() => getData(false)">
          {{ t('reload') }}
        </VBtn>
      </VCol>
    </VRow>
    <VRow class="position-relative u-parent-dragdrop pa-0 ma-0" @drop="onDropFiles" @dragover="onDragOver">
      <VCol
        v-for="(file, key) in media.getFiles"
        :key="key"
        class="d-flex child-flex cursor-pointer"
        cols="auto"
        style="width: 150px; height: 150px"
        @click="selectImage(file)"
        @dblclick="handleDoubleClick(file)">
        <VImg
          :src="getLazySrc(file, false)"
          :lazy-src="getLazySrc(file, true)"
          :alt="file.name"
          aspect-ratio="1"
          cover
          class="bg-grey-lighten-2"
          :style="data.selectedFile == file ? { border: 'solid 2px #1D5DD9' } : {}"
          @error="() => (data.uploadFileProgress[key] = false)">
          <template #placeholder>
            <ProgressBar
              v-if="!!data.uploadFileProgress?.[key]"
              class-name="ma-0"
              :size="40"
              :width="2"
              :vmodel="data.uploadFileProgress[key]"
              :indeterminate="!data.uploadFileProgress[key] ? true : null" />
          </template>
        </VImg>
      </VCol>
      <div class="image-dragdrop-mask" @dragleave="onDragLeave" />
    </VRow>
    <VRow class="pa-0 ma-0">
      <VCol cols="auto">
        <input id="file-input" multiple type="file" name="file-input" style="display: none" @change="selectedFile" />
        <VBtn :disabled="data.loading" :loading="data.loading" color="primary" class="align-center" @click="openFile">
          {{ $t('uploadImage') }}
        </VBtn>
      </VCol>
      <VCol cols="auto" class="text-center">
        <VBtn :disabled="data.loading" :loading="data.loading" color="primary" class="align-center" @click="loadMore">
          {{ $t('loadMore') }}
        </VBtn>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.image-dragdrop-mask {
  position: absolute;
  z-index: 100;
  display: none;
  align-items: center;
  justify-content: center;
  background-color: #01010154;
  block-size: 100%;
  font-size: 300%;
  inline-size: 100%;
  inset-block-start: 0;
  inset-inline-start: 0;
  text-shadow: 0 0 40px white;
}
</style>
